import { createValidate } from 'typia'
import { validateGuard } from 'components/shared/parsing/parsing.js'

export type Price = {
  consumer: string | number | null
  currency_code: string
  customer: string | number | null
  sale: string | number | null
  sale_display?: string | number | null
  sale_taxed: string | number | null
  price_per_price_uom?: string | false | null
  price_uom?: string | null
}

type Picture = {
  large: string
  mini: string
  normal: string
  small: string
}

export type ProductTag = {
  as_badge: boolean
  readonly badge_type: string | null
  name: string
  picture?: string | null
}

export type FeaturedAttributes = {
  value: string
  picture: string | null
  display_mode: 'icon' | 'text' | 'icon_text' | null
}

type VariantAttribute = {
  name: string
  value: string
  picture: string | null
  css_color: string | null
  display_mode: string
}

export type ProductVariants = {
  page_link: string
  variant: { attributes: VariantAttribute[] } | null
}

export type ProductVariantAttribute = {
  id: number
  name: string
  value: string
  value_id: number
  picture: string | null
  css_color: string | null
  display_mode: string
}

export type ProductVariant = {
  id: number
  name: string
  type: string | null
  picture?: string | null
  attributes: ProductVariantAttribute[]
}

export type StockIndicationResponse = {
  rule_id: number
  style: 'success' | 'warning' | 'error' | 'info'
  icon: string
  text: string
  tooltip?: string | null
}

export type LevyResponse = {
  description: string
  name: string
  amount_no_format: number | string | null
  amount: string
}

export type extraPictures = {
  src: string | null,
  mini: string | null,
  small: string | null,
  normal: string | null,
  large: string | null
}

export type ProductTreeResponse = {
  id: number
  name: string
  slug: string
  hidden: boolean
  parent_id: number | null
}

export type ProductResponse = {
  brand?: { name: string | null; picture?: string | null }
  code: string
  default_picture: Picture
  id: number
  name: string
  num_variants?: number | null
  ordering: {
    cannot_be_added_to_basket: boolean | null
    default_uom: string
    sale_uom?: string
    maximum_order_quantity: number | null
    minimum_order_quantity: number | null
    order_quantity: number | null
    order_quantity_dec?: string | null
    default_order_quantity_dec?: string | null
  }
  page_link: string
  picture: Picture
  extra_pictures?: extraPictures[]
  prices: Price
  rating?: { main?: { value: number | null; count: number | null } }
  stock: {
    amount: number
    message: string
    indication?: StockIndicationResponse | null
  }
  status?: { can_order_product: boolean | null } | null
  tags: ProductTag[]
  featured_attributes?: FeaturedAttributes[]
  variant?: ProductVariant | null
  variants?: ProductVariants[]
  levies?: LevyResponse[]
  trees?: ProductTreeResponse[]
  analytics_tree_path?: string[]
}

export type SortModes = {
  id: string
  name: string
  api_link: string
  search_link: string
  selected: boolean
}

export type Paginator = {
  num_pages: number
  page: number
  page_size: number
  has_prev: boolean
  has_next: boolean
  first_page_api_link?: string | null
  first_page_search_link?: string | null
  prev_page_api_link: string | null
  prev_page_search_link?: string | null
  next_page_api_link: string | null
  next_page_search_link?: string | null
  last_page_api_link?: string | null
  last_page_search_link?: string | null
}

type SearchResultsApiResponse = {
  total_found: number
  products: ProductResponse[]
  sort_modes: SortModes[]
  paginator: Paginator
}

export type SearchResultsResponse = {
  totalFound: number
  products: ProductResponse[]
  sortModes: SortModes[]
  paginator: Paginator
}

export interface SearchResults {
  products: ProductResponse[],
  totalFound: number,
  sortModes: SortModes[],
  paginator: Paginator | null,
}

const validate = createValidate<SearchResultsApiResponse>()

function mapSearchResults(raw: SearchResultsApiResponse): SearchResultsResponse {
  const { total_found, sort_modes, ...rest } = raw
  return {
    totalFound: total_found,
    sortModes: sort_modes,
    ...rest,
  }
}

export const parseSearchResults = (data: unknown): SearchResultsResponse => {
  validateGuard(validate, data, 'search results')
  return mapSearchResults(data)
}
